import { useEffect } from "react";
import { useLang } from "../context/LanguageContext";
import { useLocation } from "react-router-dom";

// Per-language SEO/head strings. Keeps titles/descriptions natural, no keyword stuffing.
const HEAD = {
  de: {
    home: {
      title: "CHEMARA — Bodenbeschichtungen in der Deutschschweiz",
      description:
        "Epoxidharz- und Polyurethanbeschichtungen, Flakes- und Effektbeschichtungen, Bodenmarkierungen, Versiegelungen und rutschhemmende Beschichtungen. Offerte unverbindlich online anfragen.",
    },
    offerte: {
      title: "Offerte anfragen — CHEMARA Bodenbeschichtungen",
      description: "Unverbindliche Offertanfrage für Ihre Bodenbeschichtung in wenigen Klicks.",
    },
    privacy: {
      title: "Datenschutz — CHEMARA",
      description: "Datenschutzerklärung von CHEMARA — chemara.ch",
    },
    imprint: {
      title: "Impressum — CHEMARA",
      description: "Impressum von CHEMARA — chemara.ch",
    },
    notfound: {
      title: "Seite nicht gefunden — CHEMARA",
      description: "Die aufgerufene Seite existiert nicht.",
    },
  },
  en: {
    home: {
      title:
        "CHEMARA — Swiss Surface Technologies, Coatings & Specialty Chemistry",
      description:
        "CHEMARA develops and manufactures premium coating systems, industrial floors and specialty chemistry — Swiss engineered for architecture and industry.",
    },
    privacy: {
      title: "Privacy — CHEMARA",
      description: "Privacy policy of CHEMARA — chemara.ch",
    },
    imprint: {
      title: "Legal Notice — CHEMARA",
      description: "Legal notice of CHEMARA — chemara.ch",
    },
    notfound: {
      title: "Page not found — CHEMARA",
      description: "The requested page does not exist.",
    },
  },
  fr: {
    home: {
      title:
        "CHEMARA — Technologies de surface suisses, revêtements & chimie spécialisée",
      description:
        "CHEMARA développe et fabrique des systèmes de revêtement premium, des sols industriels et de la chimie spécialisée — Swiss Made pour l'architecture et l'industrie.",
    },
    privacy: {
      title: "Confidentialité — CHEMARA",
      description: "Politique de confidentialité de CHEMARA — chemara.ch",
    },
    imprint: {
      title: "Mentions légales — CHEMARA",
      description: "Mentions légales de CHEMARA — chemara.ch",
    },
    notfound: {
      title: "Page introuvable — CHEMARA",
      description: "La page demandée n'existe pas.",
    },
  },
  it: {
    home: {
      title:
        "CHEMARA — Tecnologie di superficie svizzere, rivestimenti e chimica speciale",
      description:
        "CHEMARA sviluppa e produce sistemi di rivestimento premium, pavimenti industriali e chimica speciale — Swiss Made per architettura e industria.",
    },
    privacy: {
      title: "Privacy — CHEMARA",
      description: "Informativa sulla privacy di CHEMARA — chemara.ch",
    },
    imprint: {
      title: "Note legali — CHEMARA",
      description: "Note legali di CHEMARA — chemara.ch",
    },
    notfound: {
      title: "Pagina non trovata — CHEMARA",
      description: "La pagina richiesta non esiste.",
    },
  },
};

const routeKey = (pathname) => {
  if (pathname.startsWith("/privacy")) return "privacy";
  if (pathname.startsWith("/imprint")) return "imprint";
  if (pathname.startsWith("/offerte")) return "offerte";
  if (pathname === "/" || pathname === "") return "home";
  return "notfound";
};

const setMeta = (name, content, attr = "name") => {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setLink = (rel, href, extra = {}) => {
  if (!href) return;
  const selectorAttrs = Object.entries(extra)
    .map(([k, v]) => `[${k}="${v}"]`)
    .join("");
  let el = document.head.querySelector(`link[rel="${rel}"]${selectorAttrs}`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    Object.entries(extra).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const BASE = "https://chemara.ch";

export const HeadManager = () => {
  const { lang } = useLang();
  const { pathname } = useLocation();

  useEffect(() => {
    document.documentElement.lang = lang;

    const key = routeKey(pathname);
    const bucket = HEAD[lang] || HEAD.de;
    const meta = bucket[key] || bucket.home;

    // Title & description
    document.title = meta.title;
    setMeta("description", meta.description);

    // Open Graph
    setMeta("og:title", meta.title, "property");
    setMeta("og:description", meta.description, "property");
    setMeta("og:url", `${BASE}${pathname}`, "property");
    setMeta("twitter:title", meta.title);
    setMeta("twitter:description", meta.description);

    // Canonical
    setLink("canonical", `${BASE}${pathname === "/" ? "/" : pathname}`);

    // hreflang alternates only for home (single-page anchors)
    // Remove old alternates first
    document.head
      .querySelectorAll('link[rel="alternate"][hreflang]')
      .forEach((el) => el.remove());
    // (keine hreflang-Alternativen, solange nur Deutsch angeboten wird)
  }, [lang, pathname]);

  return null;
};
